import { format as formatDate } from 'date-fns';

import { Importer, type Tasks } from '@air/api';
import { type Board, type ImportFileInfo, type ImportSourceNameShared } from '@air/api/types';
import { INVALID_IMPORT_SESSION, matchesAirror } from '@air/errors';
import { formatBytes } from '@air/utilities';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type ImporterTaskEnhanced } from '~/components/Import/utils';
import {
  type ImporterFileInfoType,
  type ImporterFilesResult,
  type ImporterFolderInfoType,
  type ImporterItemsInfoType,
} from '~/swr-hooks/importer/types';
import { type BasicFileInfoType } from '~/utils/ImportUtils';

const PAGE_SIZE = 15;

const isImporterItemsInfo = (data: Record<string, unknown>): data is ImporterItemsInfoType =>
  'hash' in data && 'name' in data && 'files' in data && 'folders' in data;

export const getSourceAuthorizationUrl = async (source: ImportSourceNameShared, workspaceId: string) => {
  try {
    const { origin, pathname } = window.location;
    const cleanRedirectUrl = `${origin}${pathname}`;
    const { url } = await Importer.createAuthUrl({
      workspaceId,
      source,
      redirectUrl: cleanRedirectUrl,
    });

    return url;
  } catch (error) {
    reportErrorToBugsnag({
      error,
      context: 'Can not create authorization url',
      metadata: {
        data: {
          source,
        },
      },
    });
    return '';
  }
};

export const filesForImportFetcher = async ({
  parent,
  source,
  path,
  pageParam,
  workspaceId,
}: {
  parent: BasicFileInfoType;
  source: ImportSourceNameShared;
  path: string;
  pageParam?: string;
  workspaceId: string;
}): Promise<ImporterFilesResult> => {
  try {
    const result = await Importer.getFolder({
      source,
      descriptor: path,
      cursor: pageParam,
      pageSize: PAGE_SIZE,
      workspaceId,
    });

    if (!isImporterItemsInfo(result)) {
      throw new Error('Unexpected response shape from Importer.getFolder');
    }

    const { hash: parentHash, files, folders, cursor } = result;

    const contents: ImportFileInfo[] = [];

    files
      .filter(
        ({ hash, descriptor, importedName, recordedAt }) => !!hash && !!importedName && !!recordedAt && !!descriptor,
      )
      .forEach(({ hash, importedName, size, recordedAt, thumbnail, descriptor, ext }: ImporterFileInfoType) => {
        contents.push({
          id: hash,
          downloadKey: descriptor,
          name: importedName,
          isFile: true,
          sizeDisplay: formatBytes(size, { numberOfDecimals: 2 }),
          dateDisplay: formatDate(new Date(recordedAt), 'MM/dd/yyyy'),
          thumbnail,
          parentHash: `${parent.parentHash || parentHash}/${hash}`,
          ext,
        });
      });

    folders
      .filter(({ hash, name, descriptor }) => !!hash && !!name && !!descriptor)
      .forEach(({ hash, name, descriptor }: ImporterFolderInfoType) => {
        contents.push({
          id: hash,
          downloadKey: descriptor,
          name,
          isFile: false,
          sizeDisplay: '',
          dateDisplay: '',
          parentHash: `${parent.parentHash || parentHash}/${hash}`,
        });
      });

    return {
      cursor,
      files: contents,
      hasMore: !!cursor,
    };
  } catch (error) {
    if (matchesAirror(error, INVALID_IMPORT_SESSION)) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Can not fetch files',
      metadata: {
        data: {
          source,
          descriptor: path,
        },
      },
    });

    return {
      files: [],
      cursor: '',
      hasMore: false,
    };
  }
};

export const IMPORTER_TASK_FALLBACK_BOARDS = {
  WORKSPACE_ROOT: {
    id: '',
    title: 'All media',
    thumbnails: [] as string[],
  },
  DELETED_BOARD: {
    id: '',
    title: 'Board no longer exists',
    thumbnails: [] as string[],
  },
};

export const enhanceImporterTask = (
  task: Awaited<ReturnType<typeof Tasks.listTasks<string, 'Importer'>>>['items'][number],
  boardsMap: Map<string, Pick<Board, 'id' | 'title' | 'thumbnails'>>,
): ImporterTaskEnhanced => {
  return {
    ...task,
    metadata: {
      ...task.args,
      board: task.args.parentBoardId
        ? (boardsMap.get(task.args.parentBoardId) ?? IMPORTER_TASK_FALLBACK_BOARDS.DELETED_BOARD)
        : IMPORTER_TASK_FALLBACK_BOARDS.WORKSPACE_ROOT,
      numRootFiles: task.args.files?.length ?? 0,
      numRootFolders: task.args.folders?.length ?? 0,
    },
  };
};
